// src/admin/purge/purgeModel.js — pure shaping of the purge preview / result for the superadmin dialogs (v206).
//
// ⛔ What is NEVER shown: `paths` (absolute server paths), `collections`, `not_purgeable` and `warnings` (English
// sentences), `reappeared`, table names. The dialogs show one line per group (decision D6) and our own sentences.
// Contract: <BACKEND_FOLDER>/docs/api-server/endpoints.md#purge.

// An account a purge anonymised (the audit log names it): the admin user routes set `purged_at`. An older backend
// does not send it; its anonymised accounts read `purged-<hex>@purged.invalid` (inactive, no names) instead.
const PURGED_EMAIL_SUFFIX = '@purged.invalid';

export function isPurgedAccount(user) {
  if (user?.purged_at) return true;
  const email = user?.email;
  return typeof email === 'string' && email.toLowerCase().endsWith(PURGED_EMAIL_SUFFIX);
}

/** The first refusal code the purge would answer with now (backend refusal order), or null. */
export function firstBlocker(preview) {
  const blockers = preview?.blockers;
  return Array.isArray(blockers) && blockers.length > 0 ? blockers[0] : null;
}

function rowCount(preview, table) {
  const n = preview?.rows?.[table];
  return typeof n === 'number' ? n : 0;
}

/** Tenant preview → summary lines `{ key, params }` (admin namespace). */
export function tenantPreviewLines(preview) {
  const lines = [
    { key: 'purge.groups.runs', params: { count: rowCount(preview, 'content_generator.pipeline_runs') } },
    { key: 'purge.groups.kb', params: {} },
    { key: 'purge.groups.brand', params: {} },
  ];
  const personas = rowCount(preview, 'content_generator.persona_assist_jobs');
  if (personas > 0) lines.push({ key: 'purge.groups.personas', params: { count: personas } });
  const invitations = rowCount(preview, 'auth.membership_invitations');
  if (invitations > 0) lines.push({ key: 'purge.groups.invitations', params: { count: invitations } });
  return lines;
}

const OUTCOME_KEYS = {
  stays: 'purge.outcome.stays',
  deleted: 'purge.outcome.deleted',
  anonymised: 'purge.outcome.anonymised',
};

/** Members and what happens to each; an unknown outcome keeps its code (`outcomeKey: null`). */
export function memberLines(preview) {
  const users = Array.isArray(preview?.users) ? preview.users : [];
  return users.map((u) => ({
    email: u?.email || u?.user_id || '—',
    outcomeKey: OUTCOME_KEYS[u?.outcome] || null,
    outcome: u?.outcome || '',
  }));
}

/** Person preview → 'anonymised' | 'deleted'. */
export function personOutcome(preview) {
  return preview?.users?.[0]?.outcome === 'anonymised' ? 'anonymised' : 'deleted';
}

/** The person's tenant ids (preview) as names, from `known` rows ({tenant_id|id, display_name}); fallback the id. */
export function tenantNamesFor(preview, known) {
  const ids = preview?.users?.[0]?.tenants;
  if (!Array.isArray(ids)) return [];
  const byId = new Map((known || []).map((t) => [t?.tenant_id ?? t?.id, t?.display_name]));
  return ids.map((id) => byId.get(id) || id);
}

/** Result → how many accounts were deleted / anonymised ('stays' is not counted). */
export function resultCounts(result) {
  const users = Array.isArray(result?.users) ? result.users : [];
  return {
    deleted: users.filter((u) => u?.outcome === 'deleted').length,
    anonymised: users.filter((u) => u?.outcome === 'anonymised').length,
  };
}

/** The verification pass did not finish: re-running the purge completes it (idempotent). */
export function isIncomplete(result) {
  return result?.completed === false;
}
